import React, { useState, useEffect, useMemo } from 'react';
import {
  Layout,
  Row,
  Col,
  Card,
  Input,
  Select,
  Table,
  Tree,
  Space,
  Dropdown,
  Avatar,
  Button,
  Menu,
  Modal,
} from 'antd';
import {
  SearchOutlined,
  FolderOutlined,
  FileOutlined,
  ProfileOutlined,
  MoreOutlined,
  PlusOutlined,
  EllipsisOutlined,
  SettingOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckSquareOutlined,
  CloseSquareOutlined,
  DatabaseOutlined,
  ProjectOutlined,
  NodeIndexOutlined,
  NodeCollapseOutlined,
} from '@ant-design/icons';
import httpInjectorService from 'services/http-injector.service';
import './Spaces.css';
import { useParams, useNavigate } from 'react-router-dom';
import { useSpace } from '../hooks/useSpace';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSpaceContent } from '../hooks/useSpaceContent';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { shallowEqual } from 'react-redux';
import { setSpaceAssigneeList } from 'store/actions';
import { useDispatch, useSelector } from 'react-redux';
const { Content } = Layout;

const convertToTreeData = (list) => {
  return list.map((item) => ({
    key: item.item_id,
    title: item.title,
    item_type: item.item_type,
    parent_id: item.parent_id,
    created_by: item.created_by,
    is_stared: item.is_stared,
    is_shortcuts: item.is_shortcuts,
    status: item.status,
    updated_at: item.updated_at,
    created_by_name: item.created_by_name,

    children: item.children ? convertToTreeData(item.children) : [],
  }));
};

export default function ActiveSpace() {
  const [searchText, setSearchText] = useState('');
  const [expandedKeys, setExpandedKeys] = useState([]);
  // const [yourSpaces, setYourSpaces] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const { spaceid } = useParams();
  const { data: activeSpace, isLoading } = useSpace(spaceid);
  const { data: activeSpaceContent } = useSpaceContent(spaceid);
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [role, setRole] = useState(null);
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [creatingParentId, setCreatingParentId] = useState(null);
  const [creatingType, setCreatingType] = useState(null);
  const [newTitle, setNewTitle] = useState('');

  const [roleId, setRoleId] = useState('');

  const isAdmin = roleId === 1;

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const assigneeOptions = useSelector(
    (state) => state.Authentication?.spaceassignees,
    shallowEqual,
  );

  const addMutation = useMutation({
    mutationFn: (payload) => {
      if (payload.type === 'page') {
        return httpInjectorService.createPageDraft(payload);
      }
      if (payload.type === 'folder') {
        return httpInjectorService.createFolder(payload);
      }
      if (payload.type === 'whiteboard') {
        return httpInjectorService.createWhiteBoard(payload);
      }
      if (payload.type === 'database') {
        return httpInjectorService.createDatabase(payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['spaceContent', Number(spaceid)],
      });
    },

    onError: (error) => {
      console.error('Add failed:', error);
    },
  });

  const renameMutation = useMutation({
    mutationFn: (payload) => httpInjectorService.renameContent(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['spaceContent', Number(spaceid)],
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (payload) =>
      httpInjectorService.deleteorArchivePgorFolder(payload),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['spaceContent', Number(spaceid)],
      });
    },
  });

  const handleAdd = (record, type) => {
    setCreatingParentId(record ? record.item_id : null);
    setCreatingType(type);
    setNewTitle('');
  };

  const handleSearch = (value) => {
    setSearchText(value.toLowerCase());
  };

  const getSpaceUsers = async () => {
    try {
      const res = await httpInjectorService.getSpaceUser(spaceid);

      if (res.status === 'success') {
        dispatch(setSpaceAssigneeList(res.data));
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load users');
    }
  };

  const handleCreateSubmit = () => {
    if (!newTitle.trim()) return;

    addMutation.mutate(
      {
        parent_id: creatingParentId,
        type: creatingType,
        space_id: Number(spaceid),
        title: newTitle,
      },
      {
        onSuccess: (response) => {
          if (response?.status === 'error') {
            toast.error(response.message || 'Something went wrong', {
              autoClose: 2000,
              position: 'top-right',
            });
            return;
          }

          toast.success(response.message || 'Created successfully ', {
            autoClose: 2000,
            position: 'top-right',
          });

          setCreatingParentId(null);
          setCreatingType(null);
          setNewTitle('');
        },

        onError: (error) => {
          const msg =
            error?.response?.data?.message || error?.message || 'Server error';

          toast.error(msg, {
            autoClose: 2000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const handleDelete = (record) => {
    deleteMutation.mutate(
      {
        item_id: record.item_id,
        space_id: Number(spaceid),
        nested_item: 'yes',
        status_type: 'deleted',
        type: record.item_type,
      },
      {
        onSuccess: (response) => {
          if (response?.status === 'error') {
            toast.error(response.message || 'Delete failed ', {
              autoClose: 1000,
              position: 'top-right',
            });
            return;
          }

          toast.success(response.message || 'Deleted successfully ', {
            autoClose: 1000,
            position: 'top-right',
          });
        },

        onError: (error) => {
          const msg =
            error?.response?.data?.message ||
            error?.message ||
            'Delete failed ';

          toast.error(msg, {
            autoClose: 1000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const handleRename = (record) => {
    const trimmed = editingValue.trim();

    if (!trimmed || trimmed === record.title) {
      setEditingKey(null);
      return;
    }

    renameMutation.mutate(
      {
        item_id: record.item_id,
        title: trimmed,
        space_id: Number(spaceid),
        item_type: record.item_type,
      },
      {
        onSuccess: (response) => {
          if (response?.status === 'error') {
            toast.error(response.message || 'Rename failed ', {
              autoClose: 1000,
              position: 'top-right',
            });
            return;
          }

          toast.success(response.message || 'Renamed successfully ', {
            autoClose: 1000,
            position: 'top-right',
          });

          setEditingKey(null);
        },

        onError: (error) => {
          const msg =
            error?.response?.data?.message ||
            error?.message ||
            'Rename failed ';

          toast.error(msg, {
            autoClose: 1000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const singleSpacePermission = async () => {
    try {
      const response = await httpInjectorService.singleSpacePermission(spaceid);

      const roleId = response?.data?.[0]?.role_id;

      if (roleId) {
        setRoleId(roleId);
        Cookies.set('space-role-id', String(roleId));
      }

    
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (spaceid) {
      singleSpacePermission();
    }
  }, [spaceid]);

  const getMenu = (record) => ({
    items: [
      {
        key: 'rename',
        icon: <EditOutlined />,
        label: 'Rename',
        onClick: () => {
          setEditingKey(record.item_id);
          setEditingValue(record.title);
        },
      },
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: 'Settings',
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete',
        danger: true,
        onClick: () => handleDelete(record),
      },
    ],
  });

  // const getCreateMenu = (record) => ({
  //   items: [
  //     {
  //       key: 'add_page',
  //       icon: <FileOutlined />,
  //       label: 'Add Page',
  //       onClick: () => handleAdd(record, 'page'),
  //     },
  //     {
  //       key: 'add_folder',
  //       icon: <FolderOutlined />,
  //       label: 'Add Folder',
  //       onClick: () => handleAdd(record, 'folder'),
  //     },
  //     {
  //       key: 'add_whiteboard',
  //       icon: <ProjectOutlined />,
  //       label: 'Add Whiteboard',
  //       onClick: () => handleAdd(record, 'whiteboard'),
  //     },
  //     {
  //       key: 'add_database',
  //       icon: <DatabaseOutlined />,
  //       label: 'Add Database',
  //       onClick: () => handleAdd(record, 'database'),
  //     },
  //   ],
  // });

  const getCreateMenu = (record, features) => {
    const items = [];

    if (features?.live_docs === 1) {
      items.push({
        key: 'add_page',
        icon: <FileOutlined />,
        label: 'Add Page',
        onClick: () => handleAdd(record, 'page'),
      });
    }

    if (features?.folders === 1) {
      items.push({
        key: 'add_folder',
        icon: <FolderOutlined />,
        label: 'Add Folder',
        onClick: () => handleAdd(record, 'folder'),
      });
    }

    if (features?.whiteboards === 1) {
      items.push({
        key: 'add_whiteboard',
        icon: <ProjectOutlined />,
        label: 'Add Whiteboard',
        onClick: () => handleAdd(record, 'whiteboard'),
      });
    }

    if (features?.databases_enabled === 1) {
      items.push({
        key: 'add_database',
        icon: <DatabaseOutlined />,
        label: 'Add Database',
        onClick: () => handleAdd(record, 'database'),
      });
    }

    if (features?.smart_links === 1) {
      items.push({
        key: 'add_smart_link',
        icon: <NodeIndexOutlined />,
        label: 'Add SmartLink',
      });
    }

    if (features?.blogs === 1) {
      items.push({
        key: 'add_blogs',
        icon: <NodeCollapseOutlined />,
        label: 'Add Blogs',
      });
    }

    return { items };
  };

  // const getCreateMenuGlobal = (record) => ({
  //   items: [
  //     {
  //       key: 'add_page',
  //       icon: <FileOutlined />,
  //       label: 'Add Page',
  //       onClick: () => handleAdd(record, 'page'),
  //     },
  //     {
  //       key: 'add_folder',
  //       icon: <FolderOutlined />,
  //       label: 'Add Folder',
  //       onClick: () => handleAdd(record, 'folder'),
  //     },
  //     {
  //       key: 'add_whiteboard',
  //       icon: <ProjectOutlined />,
  //       label: 'Add Whiteboard',
  //       onClick: () => handleAdd(record, 'whiteboard'),
  //     },
  //     {
  //       key: 'add_database',
  //       icon: <DatabaseOutlined />,
  //       label: 'Add Database',
  //       onClick: () => handleAdd(record, 'database'),
  //     },
  //   ],
  // });

  const getCreateMenuGlobal = (record, features) => {
    const items = [];

    if (features?.live_docs === 1) {
      items.push({
        key: 'add_page',
        icon: <FileOutlined />,
        label: 'Add Page',
        onClick: () => handleAdd(record, 'page'),
      });
    }

    if (features?.folders === 1) {
      items.push({
        key: 'add_folder',
        icon: <FolderOutlined />,
        label: 'Add Folder',
        onClick: () => handleAdd(record, 'folder'),
      });
    }

    if (features?.whiteboards === 1) {
      items.push({
        key: 'add_whiteboard',
        icon: <ProjectOutlined />,
        label: 'Add Whiteboard',
        onClick: () => handleAdd(record, 'whiteboard'),
      });
    }

    if (features?.databases_enabled === 1) {
      items.push({
        key: 'add_database',
        icon: <DatabaseOutlined />,
        label: 'Add Database',
        onClick: () => handleAdd(record, 'database'),
      });
    }

    if (features?.smart_links === 1) {
      items.push({
        key: 'add_smart_link',
        icon: <NodeIndexOutlined />,
        label: 'Add SmartLink',
      });
    }

    if (features?.blogs === 1) {
      items.push({
        key: 'add_blogs',
        icon: <NodeCollapseOutlined />,
        label: 'Add Blogs',
      });
    }

    return { items };
  };

  const filterTree = (nodes, search) => {
    return nodes
      .map((node) => {
        const children = node.children ? filterTree(node.children, search) : [];

        if (node.title.toLowerCase().includes(search) || children.length > 0) {
          return {
            ...node,
            children,
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  const getAllUsers = async () => {
    try {
      const response = await httpInjectorService.getAllOrgUsers();
      setUsers(response.data || []);
      setIsModalOpen(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddMembers = async () => {
    if (!selectedUsers.length || !role) {
      return;
    }

    const payload = {
      space_id: spaceid,
      user_id: selectedUsers,
      role_id: role,
    };
    const response = await httpInjectorService.addUsersToSpace(payload);
    if (response.status === 'success') {
      toast.success(response.message, {
        autoClose: 1000,
        position: 'top-right',
      });
    }

    setIsModalOpen(false);
    setSelectedUsers([]);
    setRole(null);
  };

  const handleRemoveMembers = async () => {
    try {
      const payload = {
        user_id: selectedRowKeys,
        space_id: spaceid,
      };

      await httpInjectorService.removeMembersFromSpace(payload);

      toast.success('Members removed successfully');

      getSpaceUsers();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error(error);
      toast.error('Failed to remove members');
    }
  };

  const spaceMenu = (
    <Menu
      items={[
        {
          key: '1',
          label: 'Add Members',
          onClick: () => getAllUsers(),
        },
        {
          key: '2',
          label: 'All Members',
          onClick: () => {
            getSpaceUsers();
            setIsMembersModalOpen(true);
          },
        },
      ]}
    />
  );

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role_name',
      key: 'role',
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    },
  };

  return (
    <>
      <Row
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <img
          src={activeSpace?.icon_url}
          alt="icon"
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            objectFit: 'cover',
          }}
        />

        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1,
            lineHeight: '36px',
          }}
        >
          {activeSpace?.space_name}
        </h1>
        {isAdmin && (
          <Dropdown overlay={spaceMenu} trigger={['click']}>
            <Button
              type="default"
              style={{
                marginLeft: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '6px 10px',
                borderRadius: '10px',
                border: '1px solid #e5e7eb',
                background: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.2s ease',
              }}
              icon={
                <EllipsisOutlined
                  style={{
                    fontSize: 18,
                    color: '#555',
                  }}
                />
              }
            />
          </Dropdown>
        )}
      </Row>
      {activeSpace && (
        <Row
          justify="space-between"
          //align="middle"
          style={{ marginBottom: 20, marginTop: 20 }}
        >
          <Col>
            <Input
              placeholder="Search by title"
              style={{ width: 250 }}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Col>
          {isAdmin && (
            <Col>
              <Dropdown
                // disabled={selectedContent === null}
                //menu={getCreateMenuGlobal(selectedContent)}
                menu={getCreateMenuGlobal(
                  selectedContent,
                  activeSpace?.features,
                )}
              >
                <Button
                  type="default"
                  style={{
                    backgroundColor: 'purple',
                    color: 'white',
                    fontWeight: 500,
                    borderRadius: 40,
                  }}
                  icon={<PlusOutlined />}
                >
                  Add New Content
                </Button>
              </Dropdown>
            </Col>
          )}
        </Row>
      )}

      {activeSpace && (
        <Content
          style={{
            height: 'calc(100vh - 60px)',
            background: 'transparent',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 10px',
              borderBottom: '1px solid #f0f0f0',
              fontWeight: 600,
              padding: 10,
              color: 'grey',
              background: 'white',
            }}
          >
            <div
              style={{
                width: '40%',
                paddingLeft: 28,
              }}
            >
              Document Name
            </div>

            <div
              style={{
                display: 'flex',
                // gap: 20,
                width: '50%',
                justifyContent: 'flex-end',
              }}
            >
              <span style={{ width: 120 }}>Recipient</span>
              <span style={{ width: 120 }}>Status</span>
              <span style={{ width: 140 }}>Modified</span>
              {isAdmin && <span style={{ width: 60 }}>Actions</span>}
            </div>
          </div>

          <Tree
            treeData={
              searchText
                ? filterTree(activeSpaceContent, searchText)
                : activeSpaceContent
            }
            blockNode
            defaultExpandAll
            expandedKeys={expandedKeys}
            onExpand={(keys) => setExpandedKeys(keys)}
            style={{ padding: 10 }}
            titleRender={(record) => {
              const isRestrictedTemplate =
                record.item_type === 'template' &&
                record.status !== 'published';

              const showFullDetails =
                (record.item_type === 'template' &&
                  record.status === 'published') ||
                record.item_type === 'page';
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    paddingRight: 10,
                  }}
                  onClick={() => {
                    setSelectedContent(record);
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '50%',
                    }}
                  >
                    {record.item_type === 'folder' && <FolderOutlined />}
                    {record.item_type === 'page' && <FileOutlined />}
                    {record.item_type === 'whiteboard' && <ProjectOutlined />}
                    {record.item_type === 'template' && <ProfileOutlined />}
                    {record.item_type === 'database' && <DatabaseOutlined />}

                    {editingKey === record.item_id ? (
                      <Input
                        size="small"
                        value={editingValue}
                        autoFocus
                        loading={renameMutation.isPending}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onPressEnter={() => handleRename(record)}
                        onBlur={() => handleRename(record)}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        style={{ width: 200 }}
                      />
                    ) : (
                      <span
                        onClick={() => {
                          setSelectedContent(record);

                          if (
                            record.item_type === 'page' ||
                            record.item_type === 'template'
                          ) {
                            navigate(`page/${record.item_id}`);
                          }

                          if (record.item_type === 'whiteboard') {
                            navigate(`whiteboard/${record.item_id}`);
                          }

                          if (record.item_type === 'database') {
                            navigate(`database/${record.item_id}`);
                          }
                        }}
                      >
                        {record.title}
                      </span>
                    )}
                    {creatingParentId != null &&
                      creatingParentId === record.item_id && (
                        <>
                          {'/'}
                          <Input
                            size="small"
                            value={newTitle}
                            autoFocus
                            placeholder={`Enter ${creatingType} title`}
                            onChange={(e) => setNewTitle(e.target.value)}
                            onPressEnter={handleCreateSubmit}
                            style={{ width: 200, marginTop: 4 }}
                          />
                          <Button
                            style={{ width: 20, height: 22 }}
                            onClick={handleCreateSubmit}
                            type="primary"
                          >
                            <CheckSquareOutlined />
                          </Button>
                          <Button
                            style={{ width: 20, height: 22 }}
                            onClick={() => {
                              setCreatingParentId(null);
                            }}
                            color="danger"
                            variant="outlined"
                          >
                            <CloseSquareOutlined />
                          </Button>
                        </>
                      )}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      // gap: 40,
                      width: '50%',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ width: 120 }}>
                      {isRestrictedTemplate ? '-' : record.created_by_name}
                    </span>

                    <span style={{ width: 120 }}>
                      {isRestrictedTemplate ? '-' : record.status}
                    </span>

                    <span style={{ width: 140 }}>
                      {isRestrictedTemplate ? '-' : record.updated_at}
                    </span>

                    {isAdmin && (
                      <Space style={{ width: 40 }}>
                        <Dropdown
                          menu={getCreateMenu(record, activeSpace?.features)}
                        >
                          <PlusOutlined />
                        </Dropdown>

                        <Dropdown menu={getMenu(record)}>
                          <MoreOutlined />
                        </Dropdown>
                      </Space>
                    )}
                  </div>
                </div>
              );
            }}
          />

          {creatingParentId === null && creatingType && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 20px',
              }}
            >
              {/* {creatingType === 'folder' ? (
                <FolderOutlined style={{ color: '#1677ff' }} />
              ) : (
                <FileOutlined style={{ color: '#8c8c8c' }} />
              )} */}

              {creatingType === 'folder' && <FolderOutlined />}
              {creatingType === 'page' && <FileOutlined />}
              {creatingType === 'whiteboard' && <ProjectOutlined />}
              {creatingType === 'database' && <DatabaseOutlined />}

              <Input
                size="small"
                value={newTitle}
                autoFocus
                placeholder={`Enter ${creatingType} title`}
                onChange={(e) => setNewTitle(e.target.value)}
                onPressEnter={handleCreateSubmit}
                style={{ width: 200 }}
              />

              <Button
                style={{ width: 24, height: 24 }}
                onClick={handleCreateSubmit}
                type="primary"
              >
                <CheckSquareOutlined />
              </Button>

              <Button
                style={{ width: 24, height: 24 }}
                onClick={() => {
                  setCreatingParentId(null);
                  setCreatingType(null);
                }}
                danger
              >
                <CloseSquareOutlined />
              </Button>
            </div>
          )}
        </Content>
      )}

      <Modal
        title="Add Members"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleAddMembers}
        okText="Add"
        okButtonProps={{
          disabled: !selectedUsers.length || !role,
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <h6>Select Users</h6>
          <Select
            mode="multiple"
            placeholder="Select users"
            style={{ width: '100%' }}
            value={selectedUsers}
            onChange={(value) => setSelectedUsers(value)}
            options={users.map((user) => ({
              label: user.username,
              value: user.id,
            }))}
          />
        </div>

        <div>
          <h6>Select Role</h6>
          <Select
            placeholder="Select role"
            style={{ width: '100%' }}
            value={role}
            onChange={(value) => setRole(value)}
            options={[
              { label: 'Admin', value: 1 },
              { label: 'Member', value: 3 },
              { label: 'Viewer', value: 5 },
            ]}
          />
        </div>
      </Modal>

      <Modal
        title="All Members"
        open={isMembersModalOpen}
        onCancel={() => {
          setIsMembersModalOpen(false);
          setSelectedRowKeys([]);
        }}
        footer={null}
        width={700}
      >
        <div
          style={{
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            danger
            disabled={!selectedRowKeys.length}
            onClick={handleRemoveMembers}
          >
            Remove Member
          </Button>
        </div>

        <Table
          rowKey="user_id"
          columns={columns}
          dataSource={assigneeOptions || []}
          rowSelection={rowSelection}
          pagination={false}
        />
      </Modal>
    </>
  );
}
